import { Add } from 'iconsax-react';
import { type ChangeEvent, useRef } from 'react';
import useAddSticker from '@/hooks/stickers/use-add-sticker';
import useUserStickers from '@/hooks/stickers/use-user-stickers';
import { addTranslateToast } from '@/stores/toast';
import cn from '@/utils/merge-class-names';
import { convertFileToUploadable } from '@/utils/uploadable';

interface Props {
  className?: string;
}

export function StickerUploadButton({ className }: Props) {
  const { isStickerLimitReached, stickerLimit } = useUserStickers();
  const { addSticker, isPending: isLoading } = useAddSticker();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      addSticker(convertFileToUploadable(file));
    }

    event.target.value = '';
  };

  const handleClick = () => {
    if (isStickerLimitReached) {
      addTranslateToast({
        type: 'warning',
        message: `stickers.max-limit-reached|count=${stickerLimit}`,
      });
    } else if (!isLoading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <>
      <button
        type='button'
        disabled={isLoading}
        className={cn(
          'aspect-square cursor-pointer m-1 size-16 mobile:xs:m-5 rounded-lg border-2 border-dashed bg-transparent border-metal-500 flex items-center justify-center hover:bg-metal-50 transition-colors',
          isLoading && 'cursor-not-allowed',
          className,
        )}
        onClick={handleClick}>
        {isLoading ? (
          <span className='loading loading-spinner loading-md text-metal-500' />
        ) : (
          <Add className='size-8 text-metal-500' />
        )}
      </button>

      <input
        ref={fileInputRef}
        type='file'
        name='sticker-upload'
        disabled={isLoading}
        accept='image/*'
        className='sr-only'
        onChange={handleFileSelect}
      />
    </>
  );
}
